import { Link } from '@uhg-abyss/web/ui/Link';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useCallback, useContext } from 'react';

import { PortalContext } from '../../../context/PortalContext';
import { useLagoon } from '../../../hooks/useLagoon';
import { getPortalConfigs } from '../../../utils/portalConfig.utils';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { PortalName } from '../../Portal';
import { BHProviderComparisonGrid } from '../BHProviderComparisonGrid';
import {
  Divider,
  ModalOverlayStyles,
  StyledModalFooter,
  descriptionTextCss,
} from './BHProviderComparisonModal.styled';

const buildLawwMemberUrl = (contentPath: string): string => {
  const { language, accessCode } = getPortalConfigs();

  if (language && accessCode) {
    // language is 'en-US' or 'es-US'; LAWW URLs use just 'en' / 'es'
    const locale = language.split('-')[0];
    return `/${locale}/member/${accessCode}${contentPath}`;
  }

  return contentPath;
};

interface BHProviderComparisonGridModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BHProviderComparisonModal: React.FC<
  BHProviderComparisonGridModalProps
> = ({ isOpen, onClose }) => {
  const lagoonData = useLagoon('ui-messaging')();
  const { portalData } = useContext(PortalContext);
  const showFooter = portalData?.portalName === PortalName.LAWW;

  const exploreTherapyUrl = buildLawwMemberUrl(
    '/learn/explore-therapy/types-of-therapy-and-provider?source=parent'
  );

  const modalInfo = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.BH_COMPARE_PROVIDER_TYPES
      .COMPARE_PROVIDER_TYPES_MODAL_INFO,
  });
  const learnMore = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.BH_COMPARE_PROVIDER_TYPES
      .LEARN_MORE_ABOUT_PROVIDERS,
  });

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <ModalDialog
      closeOnClickOutside={true}
      css={ModalOverlayStyles}
      data-testid="bh-provider-comparison-modal"
      footer={
        showFooter ? (
          <StyledModalFooter data-testid="bh-provider-comparison-footer">
            <Link
              afterIcon={learnMore?.icon}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.open(exploreTherapyUrl, '_blank', 'noopener,noreferrer');
              }}
              size="md"
            >
              <Text color="$primary1" fontWeight="bold">
                {learnMore?.message}
              </Text>
            </Link>
          </StyledModalFooter>
        ) : undefined
      }
      header={modalInfo?.title}
      isOpen={isOpen}
      onClose={handleClose}
      size="75%"
    >
      <Text css={descriptionTextCss} fontSize="lg" fontWeight="regular">
        {modalInfo?.message}
      </Text>
      <BHProviderComparisonGrid onClose={handleClose} />
      {showFooter && <Divider />}
    </ModalDialog>
  );
};
